import { useState, useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { HiDotsVertical, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { AiOutlineClose, AiOutlineUser, AiOutlineCloudDownload } from "react-icons/ai";
import { MdArrowForwardIos } from "react-icons/md";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { find, includes, map, remove } from "lodash";
import { toast } from "react-hot-toast";

import BulkLoadModal from "./bulk-load-modal";
import InvitationModal from "./invitation-modal";
import EditCollaboratorModal from "../../collaborator-details/components/edit-modal";
import CreateCollaboratorModal from "../../collaborator-details/components/create-modal";
import TableComponent from "../../../../components/table/table-component";
import { collaboratorsAtom } from "../../../../services/atoms/collaborators.atoms";
import { useCollaboratorsActions } from "../../../../services/actions/collaborators.actions";
import { ButtonLoading } from "../../../../components/buttons/button-loading";
import { ButtonIcon } from "../../../../components/buttons/button-icon";
import { InputIcon } from "../../../../components/inputs/input-icon";
import { ROUTES } from "../../../../helpers/constants";
import { formatRut } from "rutlib/lib";
import BulkLoadErrors from "./bulk-load-modal-errors";
import CreateUserAdminModal from "../../usersadmin/components/create-modal";
import UsersAdmin from "../../usersadmin";
import { useUsersAdminAction } from "../../../../services/actions/users-admin.actions";
import { usersAdminAtom } from "../../../../services/atoms/users-admin.atom";
import ColorsCircles from "../../params/components/colors-roles";
import { colorsFromResults } from "../../../../helpers/functions";
import { TestTypeService } from "../../../../utils/test-type-registry";
import SortDropdown from "../../../../components/dropdown/sort-dropdown";

const HIDE_DISC = true;

const options = [
  { id: 'opt-1', title: 'Editar Colaborador', action: 'edit', icon: HiOutlinePencil },
  { id: 'opt-2', title: 'Eliminar Colaborador', action: 'remove', icon: HiOutlineTrash },
];

const badgeAccessor = (row) => (<div className='gm-badge'><span>{row || 'No informada'}</span> </div>);

const findLatestFinishedTest = (tests, isTestGrowmeResult = false) => {
  if (isTestGrowmeResult) {
    return tests.find((test) => test.statusId === 4 && TestTypeService.isTestCategory(test, 'competency'));
  }
  
  return tests.find((test) => test.statusId === 4 && TestTypeService.isTestCategory(test, 'disc'));
};

const renderColorCircles = (colorsForCircles) => (
  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: "center" }}>
    {colorsForCircles.map((colors, index) => (
      <div key={`${index}-${colors}`}>
        <ColorsCircles colors={colors} />
      </div>
    ))}
  </div>
);

const renderEmptyColorCircles = () => (
  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: "center" }}>
    <ColorsCircles /> <ColorsCircles />
  </div>
);

const findLatestUnifiedTest = (tests) => {
  return tests.find((test) => test.statusId >= 4 && TestTypeService.isTestCategory(test, 'unified'));
};

const unifiedTestColorAccessor = (row) => {
  const latestUnifiedTest = findLatestUnifiedTest(row);
  if (!latestUnifiedTest) {
    return renderEmptyColorCircles();
  }
  const colorsForCircles = colorsFromResults(latestUnifiedTest.results, false, true);
  return renderColorCircles(colorsForCircles);
};

const combinedGrowmeAccessor = (row) => {
  const latestUnified = findLatestUnifiedTest(row);
  const latestCompetency = findLatestFinishedTest(row, true);
  const testToUse = latestUnified || latestCompetency;
  if (!testToUse) {
    return renderEmptyColorCircles();
  }
  const colorsForCircles = colorsFromResults(testToUse.results, false, true);
  return renderColorCircles(colorsForCircles);
};

const resultsColorAccessor = (row, isTestGrowmeResult = false) => {
  const isBoundedDisc = !isTestGrowmeResult && row.length === 2;
  const latestFinishedTest = findLatestFinishedTest(row, isTestGrowmeResult);
  if (!latestFinishedTest) {
    return renderEmptyColorCircles();
  }
  const colorsForCircles = colorsFromResults(
    latestFinishedTest.results,
    isBoundedDisc,
    isTestGrowmeResult
  );
  return renderColorCircles(colorsForCircles);
};

const columns = [
  { Header: 'Nombre Completo', accessor: ({ name, surname }) => <div>{`${name} ${surname}`}</div> },
  { Header: 'Correo', accessor: ({ email }) => <div>{email}</div> },
  { Header: 'Cargo a postular', accessor: ({ position }) => badgeAccessor(position) },
  // { Header: 'Área', accessor: 'area', disableFilters: false },
  { Header: 'Área', accessor: ({ area }) => <div>{area || 'No informada'}</div> },
  { Header: 'Origen', accessor: ({ collaboratorInternal }) => <div>{collaboratorInternal ? 'Interno' : 'Externo'}</div> },
  ...(!HIDE_DISC ? [
    { Header: 'Test Disc', accessor: ({ latestTest }) => resultsColorAccessor(latestTest)},
  ] : []),

  // New unified test should be shown as 'Test Growme'
  // If both unified and legacy Growme exist, show unified; otherwise show whichever is available
  { Header: 'Test Growme', accessor: ({ latestTest }) => combinedGrowmeAccessor(latestTest) },
  { Header: 'Acciones', accessor: '', disableFilters: true, disableSortBy: true, type: 'actions' },
];

const initialCollaboratorData = {
  id: '',
  name: "",
  surname: "",
  rut: "",
  email: "",
  area: "",
  position: "",
}

const initialSetOpenState = {
  bulk: false,
  invitation: false,
  create: false,
  edit: false,
  errors: false,
  createUserAdmin: false,
  editUserAdmin: false
};

const initialSearchValue = { name: '', position: '' };

// Las claves deben coincidir con COLLABORATORS_SORT_OPTIONS del back
// (TypeOrmCollaboratorRepository).
const sortOptions = [
  { value: 'created_recent', label: 'Creados más recientes' },
  { value: 'created_oldest', label: 'Creados más antiguos' },
  { value: 'recent', label: 'Test enviado más reciente' },
  { value: 'oldest', label: 'Test enviado más antiguo' },
  { value: 'finished_recent', label: 'Último test finalizado' },
  { value: 'name_asc', label: 'Nombre (A - Z)' },
  { value: 'name_desc', label: 'Nombre (Z - A)' },
];
const SORT_STORAGE_KEY = 'gm-collaborators-sort';
const DEFAULT_SORT = 'name_asc';

const getStoredSort = () => {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    return sortOptions.some((option) => option.value === stored) ? stored : DEFAULT_SORT;
  } catch {
    return DEFAULT_SORT;
  }
};

const TableSection = ({ navigate, client, onTestSent }) => {
  const {
    resetCollaboratorsList,
    updateCollaborator,
    collaboratorsList,
    downloadReports,
    resetInvitation,
    resetBulkLoad,
    deleteCollaborator
  } = useCollaboratorsActions();
  const {usersAdminsList} = useUsersAdminAction()
  const [usersAdmin, setUsersAdmin] = useRecoilState(usersAdminAtom);
  const collaborators = useRecoilValue(collaboratorsAtom);
  const [collaborator, setCollaborator] = useState(initialCollaboratorData);
  const [checkedItems, setCheckItem] = useState([]);
  const [allItemsChecked, setAllItemsChecked] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchExists, setSearch] = useState('');
  const [searchValues, setSearchValue] = useState(initialSearchValue);
  const [open, setOpen] = useState(initialSetOpenState);
  const [isInternal, setInternal] = useState(true)
  const [viewSelected, setViewSelected] = useState('collaborators')
  const [errorsModal, setErrorsModal] = useState([])
  const [sort, setSort] = useState(getStoredSort);
  const [activeSearch, setActiveSearch] = useState(null);

  // Recarga la lista respetando el orden elegido y la búsqueda/filtro activo.
  const refreshCollaborators = (search = activeSearch) =>
    collaboratorsList({ page, limit, clientId: client.id, search, sort });

  useEffect(() => {
    refreshCollaborators();
    // eslint-disable-next-line
  }, [page, limit, sort]);

  useEffect(() => {
    return () => resetCollaboratorsList()
    // eslint-disable-next-line
  }, []);

  const handleChangeSort = (value) => {
    setSort(value);
    setPage(1);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, value);
    } catch {
      // Sin acceso a localStorage: el orden se aplica igual, solo no se recuerda.
    }
  };

  useEffect(() => {
    !open.bulk && resetBulkLoad();
    !open.invitation && resetInvitation();
    // eslint-disable-next-line
  }, [open]);

  useEffect(()=>{
  usersAdminsList(client.name)
  }, [])


  const handleChangePage = (e) => setPage(e + 1);
  const handleSetLimit = (e) => setLimit(e);
  const handleOpenDetails = (e, index) => {
    navigate(`${ROUTES.CLIENTS.route}/collaborator/${e.id}`, { state: { collaborator: e, client, index: `${index}` } });
  }
  const handleOptionAction = (action, row) => {
    if (action === 'edit') {
      setCollaborator(row);
      setOpen(modal => ({ ...modal, edit: true }))
    };
    if(action==='remove'){
      setCollaborator(row)
      handleDeleteCollaborator(row)
    }
  };

  const actions = [
    { icon: HiDotsVertical, dropdown: true },
    { icon: MdArrowForwardIos, action: handleOpenDetails },
  ];

  const downloadReportsAction = () => {
    toast('Preparando descarga...', {
      icon: '😎',
    });
    downloadReports({
      collaboratorsIds: checkedItems.map(i => i.id),
    }, ({ signedUrl, testsFinished }) => {
      if (testsFinished) {
        toast.success(`Pruebas encontradas ${testsFinished}`);
        window.open(signedUrl, '_blank');
      } else {
        toast('Aun no hay pruebas finalizadas!', {
          icon: '😱',
        });
      }
    });
  };

  const handleOpenModal = (key) => {
    if (key === 'invitation' && !checkedItems.length) {
      return toast('Debe seleccionar al menos un colaborador.', {icon: '🎯'});
    }
    setOpen(modal => ({ ...modal, [key]: !modal[key] }));
  };

  const handleChangeValue = (key, value) => setSearchValue((prevValues) => ({ ...prevValues, [key]: value }));

  const handleSearchByName = (e, key) => {
    e.preventDefault();
    const data = new FormData(e.target);
    let form = Object.fromEntries(data.entries());
    let search = { column: `collaborator.${key}`, value: form['search'] };
    setSearch(form['search']);
    setActiveSearch(search);
    refreshCollaborators(search);
  };

  const handleChangeOrigin = (event) => {
    const selectedValue = event.target.value;
    if(selectedValue !== "false"){
      const collaboratorField = selectedValue === "collaboratorInternal" ? "collaboratorInternal" : "collaboratorExternal";
      let search= { column: `collaborator.${collaboratorField}`, value: true }
  
      setSearch(selectedValue);
      setActiveSearch(search);
      refreshCollaborators(search);
    } else{
      return;
    }
  };

  const handleCleanSearch = () => {
    setSearch('');
    setSearchValue(initialSearchValue);
    setActiveSearch(null);
    refreshCollaborators(null);
    resetSelect();
  }

  const resetSelect = () => {
    const select = document.querySelector('select[name="origen"]');
    select.selectedIndex = 0;
  };

  const handleAllChecked = (event) => {
    setAllItemsChecked(event.target.checked);
    let collabs = collaborators?.payload || [];
    event.target.checked ?
      setCheckItem(map(collabs, (e) => ({ ...e, checked: true }))) :
      setCheckItem([]);
  }

  const handleCheckOne = (checked, row) => {
    setAllItemsChecked(false);
    let items = [...checkedItems];
    find(items, e => e.id === row.id) ? remove(items, (e) => e.id === row.id) : items.push({ ...row, checked });
    setCheckItem(items);
  }

  const handleSubmitEdit = (e) => {
    e.preventDefault();
    const phoneNumber = collaborator?.phone
    const dataToSend={
      ...collaborator,
      collaboratorInternal: isInternal,
      collaboratorExternal: !isInternal, 
      rut: formatRut(collaborator.rut),
      phone: phoneNumber == undefined ? "" : phoneNumber
    }
    updateCollaborator(dataToSend, () => {
      toast.success('Colaborador actualizado exitosamente');
      handleOpenModal('edit');
      refreshCollaborators();
    });
  }

  const handleConfirm = (toastId, row) => {
    deleteCollaborator(row.id, (res) => {
      if (includes(res?.message, 'Request failed with status')) {
        toast.error('El colaborador no se ha podido eliminar', {duration:4000});
        toast.dismiss(toastId);
      } else {
        toast.success('Colaborador eliminado exitosamente', {duration:4000});
        toast.dismiss(toastId);
        refreshCollaborators();
      }
    });
  };

  const handleCancel = (toastId) => {
    toast.dismiss(toastId);
  };

  const handleDeleteCollaborator = (row) => {
  toast((t) => (
    <div className="gm-modal-form-group">
      <p style={{color:'#003849'}}>¿Estás seguro de que quieres eliminar este colaborador?</p>
      <div style={{display:'flex', justifyContent:'space-around'}}>
      <button
        className="gm-secondary-button"
        onClick={() => handleCancel(t.id)}>
        Cancelar
      </button>
      <button className="gm-primary-button" onClick={() => handleConfirm(t.id, row)}>
        Confirmar
      </button>
      </div>
    </div>
  ), {duration: 6000});
  }

  return (
    <div className="gm-table-section-container">
      {open.errors && (
        <BulkLoadErrors
        open={open.errors}
        toggle={() => handleOpenModal("errors")}
        errorsModal={errorsModal}
        />
      )}
      {open.edit && (
        <EditCollaboratorModal
          collaboratorHandler={{
            collaborator,
            setCollaborator,
            isInternal,
            setInternal,
          }}
          open={open.edit}
          submit={handleSubmitEdit}
          toggle={() => handleOpenModal("edit")}
          clientId={client?.id || ""}
        />
      )}
      {open.bulk && (
        <BulkLoadModal
          open={open.bulk}
          toggle={() =>{
            handleOpenModal("bulk")
          } }
          clientId={client?.id || ""}
          update={()=>{refreshCollaborators();}}
          pageMeta={{ page, limit }}
          handleOpenModal={()=>handleOpenModal('errors')}
          setErrorsModal={setErrorsModal}
        />
      )}
      {open.invitation && (
        <InvitationModal
          open={open.invitation}
          toggle={() => handleOpenModal("invitation")}
          checkedItems={checkedItems}
          allItems={collaborators?.payload}
          onTestSent={onTestSent}
        />
      )}
      {open.create && (
        <CreateCollaboratorModal
          open={open.create}
          toggle={() => handleOpenModal("create")}
          clientId={client?.id || ""}
          update={() => refreshCollaborators()}
          handleOpenModal={handleOpenModal}
          bulkLoad
        />
      )}
      {open.createUserAdmin && (
        <CreateUserAdminModal
          open={open.createUserAdmin}
          toggle={() => handleOpenModal("createUserAdmin")}
          clientId={client?.id || ""}
          clientName={client.name || ""}
        />
      )}
      {/* <div className="gm-ts-title">Colaboradores</div> */}
      <div className="gm-ts-searchbar-container">
        <div className="gm-ts-searchbar-title-container">
          <div className="gm-ts-searchbar-title">
            <button
              className={viewSelected === 'collaborators' ? "gm-ts-searchbar-button" : 'gm-ts-searchbar-button-inactive'}
              onClick={() => {
                setViewSelected("collaborators");
              }}
            >{`Colaboradores (${collaborators?.payload.length || 0})`}</button>
          </div>
          <div className="gm-ts-searchbar-title">
            <button
              className={viewSelected === 'users' ? "gm-ts-searchbar-button" : 'gm-ts-searchbar-button-inactive'}
              onClick={() => {
                setViewSelected("users");
              }}
            >{`Usuarios (${usersAdmin?.payload.length})`}</button>
          </div>
        </div>

        {viewSelected === "collaborators" && (
          <div className="gm-ts-searchbar-actions-container">
            {searchExists && (
              <button
                className="gm-clean-search-button"
                onClick={handleCleanSearch}
                type="button"
              >
                <AiOutlineClose size={18} />
              </button>
            )}
            <form onSubmit={(e) => handleSearchByName(e, "name")}>
              <InputIcon
                Icon={HiMagnifyingGlass}
                type="text"
                placeholder="Buscar por nombre"
                name="search"
                value={searchValues.name}
                onChange={(e) => handleChangeValue("name", e.target.value)}
              />
            </form>
            <form>
              <div className="gm-input-icon-container">
                <select
                  name="origen"
                  onChange={(event) => {
                    handleChangeOrigin(event);
                  }}
                >
                  <option value={false}>Filtrar por origen</option>
                  <option value="collaboratorInternal">Interno</option>
                  <option value="collaboratorExternal">Externo </option>
                </select>
              </div>
            </form>
            <SortDropdown
              options={sortOptions}
              value={sort}
              onChange={handleChangeSort}
              disabled={collaborators.loading}
            />
            <ButtonIcon
              Icon={AiOutlineUser}
              buttonLabel="Cargar Colaboradores"
              type="button"
              className="gm-outline-button"
              onClick={() => handleOpenModal("create")}
            />
            {checkedItems.length > 0 && (
              <ButtonIcon
                Icon={AiOutlineCloudDownload}
                buttonLabel="Descargar Informes"
                type="button"
                className="gm-outline-button"
                onClick={() => downloadReportsAction()}
              />
            )}
            <ButtonLoading
              className="gm-primary-button"
              type="button"
              buttonLabel="Enviar Invitación"
              onClick={() => handleOpenModal("invitation")}
              disabled={!collaborators.payload?.length || collaborators.loading}
              loading={collaborators.loading}
            />
          </div>
        )}

        {viewSelected === "users" && (
          <div className="gm-ts-searchbar-actions-container">
            {searchExists && (
              <button
                className="gm-clean-search-button"
                onClick={handleCleanSearch}
                type="button"
              >
                <AiOutlineClose size={18} />
              </button>
            )}
            <form onSubmit={(e) => handleSearchByName(e, "name")}>
              <InputIcon
                Icon={HiMagnifyingGlass}
                type="text"
                placeholder="Buscar por nombre"
                name="search"
                value={searchValues.name}
                onChange={(e) => handleChangeValue("name", e.target.value)}
              />
            </form>
            <ButtonIcon
              Icon={AiOutlineUser}
              buttonLabel="Crear Usuario"
              type="button"
              className="gm-outline-button"
              onClick={() => handleOpenModal("createUserAdmin")}
            />
          </div>
        )}
      </div>
      {viewSelected === "collaborators" && (
        <TableComponent
          // La tabla guarda su pageIndex internamente: al cambiar el orden se
          // remonta para volver a la página 1 junto con el estado `page`.
          key={sort}
          data={collaborators?.payload || []}
          loading={collaborators.loading}
          emptyListInformer="small"
          columns={columns}
          handleOptionAction={handleOptionAction}
          handleOpenDetails={handleOpenDetails}
          handleChangePage={handleChangePage}
          handleSetLimit={handleSetLimit}
          controlledPageCount={
            collaborators?.total ? Math.ceil(collaborators.total / limit) : 1
          }
          actions={actions}
          options={options}
          section={{ plural: "colaboradores", singular: "colaborador" }}
          checkbox={true}
          itemChecked={checkedItems}
          allItemsChecked={allItemsChecked}
          handleCheckOne={handleCheckOne}
          handleAllChecked={handleAllChecked}
          manualPagination={true}
          pagination
        />
      )}

      {
        viewSelected === "users" && (
          <UsersAdmin
            clientName={client.name || ""}
          />
        )
      }
    </div>
  );
}

export default TableSection;